import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Loader2, 
  Check, 
  X, 
  BookOpen,
  Plus,
  BookText,
  History,
  Edit3,
  CheckCircle2,
  TrendingUp,
  Flame,
  BarChart3,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressionModal = ({ isOpen, onClose }) => {
  const [progressData, setProgressData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dailyGoal, setDailyGoal] = useState(10);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [tempGoal, setTempGoal] = useState(10);

  useEffect(() => {
    if (isOpen) {
      fetchProgress();
    }
  }, [isOpen]);

  const fetchProgress = async () => {
    setIsLoading(true);
    try {
      const [progressRes, goalRes] = await Promise.all([
        axios.get(`${API}/progress/daily`),
        axios.get(`${API}/progress/goal`)
      ]);
      setProgressData(progressRes.data);
      setDailyGoal(goalRes.data.daily_goal);
      setTempGoal(goalRes.data.daily_goal);
    } catch (err) {
      console.error("Error fetching progress:", err);
      toast.error("Erreur lors du chargement des progrès");
    } finally {
      setIsLoading(false);
    }
  };

  const saveGoal = async () => {
    try {
      await axios.post(`${API}/progress/goal`, { daily_goal: tempGoal });
      setDailyGoal(tempGoal);
      setIsEditingGoal(false);
      toast.success(`Objectif mis à jour : ${tempGoal} mots/jour`);
      fetchProgress();
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const getStreakEmoji = (streak) => {
    if (streak >= 30) return "🏆";
    if (streak >= 14) return "🔥";
    if (streak >= 7) return "⭐";
    if (streak >= 3) return "✨";
    return "🌱";
  };

  const getGoalColor = (percentage) => {
    if (percentage >= 100) return "from-green-500 to-emerald-500";
    if (percentage >= 75) return "from-yellow-500 to-amber-500";
    if (percentage >= 50) return "from-orange-500 to-amber-500";
    return "from-red-500 to-orange-500";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-indigo-50 to-purple-50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-800 text-xl">
            <BarChart3 className="w-6 h-6" />
            Ma Progression
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
          </div>
        ) : progressData ? (
          <div className="space-y-6">
            {/* Manee's Message */}
            <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl p-4 border border-pink-200">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-xl">
                  ม
                </div>
                <div>
                  <p className="font-medium text-purple-800">Manee te dit :</p>
                  <p className="text-purple-700 font-thai">{progressData.ploy_message}</p>
                </div>
              </div>
            </div>

            {/* Daily Stats */}
            <div className="grid grid-cols-3 gap-3">
              {/* Words Practiced */}
              <div className="bg-white rounded-2xl p-4 border border-indigo-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm">Mots pratiqués</span>
                  <BookOpen className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="text-3xl font-bold text-indigo-600">
                  {progressData.words_practiced}
                </div>
                <div className="text-xs text-slate-500">
                  clics/écoutes
                </div>
              </div>

              {/* Words Added */}
              <div className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm">Mots ajoutés</span>
                  <Plus className="w-5 h-5 text-green-500" />
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {progressData.words_added}
                </div>
                <div className="text-xs text-slate-500">
                  nouveau vocabulaire
                </div>
              </div>

              {/* Stories Read */}
              <div className="bg-white rounded-2xl p-4 border border-purple-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm">Histoires</span>
                  <BookText className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {progressData.stories_read}
                </div>
                <div className="text-xs text-slate-500">
                  lues aujourd'hui
                </div>
              </div>

              {/* Streak */}
              <div className="bg-white rounded-2xl p-4 border border-orange-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm">Série</span>
                  <Flame className="w-5 h-5 text-orange-500" />
                </div>
                <div className="text-3xl font-bold text-orange-600">
                  {progressData.streak_days} {getStreakEmoji(progressData.streak_days)}
                </div>
                <div className="text-xs text-slate-500">
                  jours consécutifs
                </div>
              </div>

              {/* Time Spent */}
              <div className="bg-white rounded-2xl p-4 border border-cyan-100 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm">Temps passé</span>
                  <History className="w-5 h-5 text-cyan-500" />
                </div>
                <div className="text-3xl font-bold text-cyan-600">
                  {progressData.time_spent_minutes}
                </div>
                <div className="text-xs text-slate-500">
                  minutes estimées
                </div>
              </div>
            </div>

            {/* Total Progress */}
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-4 border border-indigo-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-700">Total activités</p>
                  <p className="text-2xl font-bold text-indigo-800">
                    {progressData.words_practiced + progressData.words_added + progressData.stories_read}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-indigo-700">Objectif</p>
                  <p className="text-2xl font-bold text-indigo-800">{dailyGoal}</p>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white rounded-2xl p-5 border border-indigo-100 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-slate-700">Objectif du jour</span>
                <span className={`font-bold ${progressData.goal_percentage >= 100 ? 'text-green-600' : 'text-indigo-600'}`}>
                  {Math.round(progressData.goal_percentage)}%
                </span>
              </div>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${getGoalColor(progressData.goal_percentage)} transition-all duration-500`}
                  style={{ width: `${Math.min(100, progressData.goal_percentage)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>{progressData.words_practiced} mots</span>
                <span>Objectif: {dailyGoal} mots</span>
              </div>
            </div>

            {/* Weekly Chart */}
            <div className="bg-white rounded-2xl p-5 border border-indigo-100 shadow-sm">
              <h3 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />
                Cette semaine
              </h3>
              <div className="flex items-end justify-between gap-2 h-32">
                {progressData.weekly_data.map((day, idx) => {
                  const maxWords = Math.max(...progressData.weekly_data.map(d => d.words), dailyGoal);
                  const height = maxWords > 0 ? (day.words / maxWords * 100) : 0;
                  const isToday = idx === progressData.weekly_data.length - 1;
                  
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col items-center">
                        <span className="text-xs text-slate-500 mb-1">{day.words}</span>
                        <div 
                          className={`w-full rounded-t-lg transition-all ${
                            day.goal_reached 
                              ? 'bg-gradient-to-t from-green-500 to-emerald-400' 
                              : isToday
                                ? 'bg-gradient-to-t from-indigo-500 to-purple-400'
                                : 'bg-gradient-to-t from-slate-300 to-slate-200'
                          }`}
                          style={{ height: `${Math.max(height, 5)}px` }}
                        />
                      </div>
                      <span className={`text-xs ${isToday ? 'font-bold text-indigo-600' : 'text-slate-500'}`}>
                        {day.day_name}
                      </span>
                      {day.goal_reached && (
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-500" />
                  <span>Objectif atteint</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-slate-300" />
                  <span>En cours</span>
                </div>
              </div>
            </div>

            {/* Goal Settings */}
            <div className="bg-white rounded-2xl p-5 border border-indigo-100 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-500" />
                  <span className="font-medium text-slate-700">Objectif quotidien</span>
                </div>
                {isEditingGoal ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={tempGoal}
                      onChange={(e) => setTempGoal(parseInt(e.target.value) || 1)}
                      className="w-16 px-2 py-1 border rounded text-center"
                    />
                    <span className="text-sm text-slate-500">mots/jour</span>
                    <Button size="sm" onClick={saveGoal} className="bg-green-500 hover:bg-green-600">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setIsEditingGoal(false);
                      setTempGoal(dailyGoal);
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-indigo-600">{dailyGoal} mots/jour</span>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingGoal(true)}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {[5, 10, 20, 30].map(goal => (
                  <button
                    key={goal}
                    onClick={() => {
                      setTempGoal(goal);
                      setIsEditingGoal(true);
                    }}
                    className={`py-2 rounded-lg text-sm font-medium transition-all ${
                      dailyGoal === goal
                        ? 'bg-indigo-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-indigo-100'
                    }`}
                  >
                    {goal} mots
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            Aucune donnée disponible
          </div>
        )}

        <Button
          variant="outline"
          onClick={onClose}
          className="w-full mt-4 rounded-xl"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ProgressionModal;
